import * as React from "react";
import { useLocation } from "react-router-dom";
import { AppSidebar } from "@/components/app-sidebar";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

/**
 * Deterministic canvas gradient-position offset by major app area
 * (zero runtime randomness). Modeling → a, Reporting → b,
 * Administration → c, Budgets/default → base.
 */
function textureOffsetForPath(pathname: string): string | undefined {
  if (pathname.startsWith("/models")) return "texture-offset-a";
  if (pathname.startsWith("/reporting")) return "texture-offset-b";
  if (
    pathname.startsWith("/users") ||
    pathname.startsWith("/security") ||
    pathname.startsWith("/dimensions")
  ) {
    return "texture-offset-c";
  }
  return undefined;
}

/**
 * The authenticated app shell: sidebar + inset content area. Single source of
 * truth used by every route guard, so the layout is defined in exactly one place.
 *
 * - h-svh bounds the shell to the viewport, so pages fill it with h-full instead
 *   of guessing a magic vh value.
 * - min-h-0 / min-w-0 let the inset shrink so a page's own scroll containers work,
 *   instead of the inset overflowing the viewport (SidebarInset ships with
 *   min-width:auto, which lets wide content like the model grid push it off-screen).
 */
export function AppShell({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const textureOffset = textureOffsetForPath(pathname);

  return (
    <SidebarProvider className={cn("h-svh", textureOffset)}>
      <AppSidebar />
      <SidebarInset className="min-h-0 min-w-0">{children}</SidebarInset>
    </SidebarProvider>
  );
}
